function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// ----------------------------------------------------------------------------
// Helper: Storefront API GraphQL caller
// ----------------------------------------------------------------------------

import { createStorefrontApiClient } from '@shopify/storefront-api-client';
import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';
class StorefrontApiError extends Error {
  constructor(message, data, errors, extensions) {
    super(message);
    this.data = data;
    this.errors = errors;
    this.extensions = extensions;
  }
}
class StorefrontApi {
  /**
  * @constructor
  *
  * @param {string} [storeDomain] Store domain.
  * @param {string} [apiVersion] API version.
  * @param {string} [publicAccessToken] Public Access Token
  *                 (WARNING: use a token with "Unathenticated" access only, otherwise it could be a major security risk)
  */
  constructor() {
    var _window$theme,
      _this = this;
    var storeDomain = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : "https://".concat(window.Shopify.shop);
    var apiVersion = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '2025-01';
    var publicAccessToken = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : (_window$theme = window.theme) === null || _window$theme === void 0 ? void 0 : _window$theme.storefrontApiAccessToken;
    _defineProperty(this, "client", null);
    /**
    * Returns JSON response based on GraphQL query from the Shopify Storefront API.
    *
    * @param {string} query - The GraphQL query string.
    * @param {object} [variables={}] - The variables for the GraphQL query.
    * @returns {Promise<object>} API json response.
    */
    _defineProperty(this, "request", /*#__PURE__*/function () {
      var _ref = _asyncToGenerator(function* (query) {
        var variables = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
        var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
        if (!_this.client) {
          console.error('StorefrontAPI client is not initialized. Cannot make request.');
          return; // Prevent further execution of the request
        }
        query = yield iamotaEventHandler.applyFilters('Shopify:StorefrontAPI:request:query', query.trim(), {
          variables,
          options
        });
        variables = yield iamotaEventHandler.applyFilters('Shopify:StorefrontAPI:request:variables', variables, {
          query,
          options
        });
        if (Object.keys(variables).length > 0) {
          options.variables = variables;
        }
        options = yield iamotaEventHandler.applyFilters('Shopify:StorefrontAPI:request:options', options, {
          query,
          variables
        });
        iamotaEventHandler.trigger('Shopify:StorefrontAPI:requesting', {
          query,
          variables,
          options
        });
        return _this.client.request(query, options).then(_ref2 => {
          var data = _ref2.data,
            errors = _ref2.errors,
            extensions = _ref2.extensions;
          // Optionally simulate slow network
          var delay = parseInt(localStorage.getItem('iamotaSimulateSlow') || 0, 10);
          return new Promise(resolve => {
            setTimeout(() => {
              resolve({
                data,
                errors,
                extensions
              });
            }, delay);
          });
        }).then(_ref3 => {
          var data = _ref3.data,
            errors = _ref3.errors,
            extensions = _ref3.extensions;
          if (errors) {
            iamotaEventHandler.trigger('Shopify:StorefrontAPI:requested:errors', {
              data,
              errors,
              extensions
            });
            throw new StorefrontApiError(errors.message, data, errors, extensions);
          }
          if (!data) {
            iamotaEventHandler.trigger('Shopify:StorefrontAPI:requested:noData', {
              data,
              errors,
              extensions
            });
            throw new StorefrontApiError('No data returned from the API.', data, errors, extensions);
          }
          return iamotaEventHandler.applyFilters('Shopify:StorefrontAPI:requested', {
            data,
            extensions
          });
        }).catch(error => {
          console.error("StorefrontAPI Error: ".concat(error.message), error);
          iamotaEventHandler.trigger('Shopify:StorefrontAPI:error', {
            error,
            query,
            variables
          });
          throw error;
        }).finally(() => {
          iamotaEventHandler.trigger('Shopify:StorefrontAPI:finally');
        });
      });
      return function (_x) {
        return _ref.apply(this, arguments);
      };
    }());
    _defineProperty(this, "getProductMetafield", /*#__PURE__*/function () {
      var _ref4 = _asyncToGenerator(function* (product_id, namespace, key) {
        var query = "\n            query($id: ID!, $namespace: String!, $key: String!) {\n                product(id: $id) {\n                    metafield(namespace: $namespace, key: $key) {\n                        value\n                    }\n                }\n            }\n        ";
        var variables = {
          id: "gid://shopify/Product/".concat(product_id),
          namespace,
          key
        };
        return _this.request(query, variables).then(_ref5 => {
          var data = _ref5.data,
            errors = _ref5.errors,
            extensions = _ref5.extensions;
          if (errors) throw new Error("".concat(errors.message, " [").concat(errors.graphQLErrors.map(error => error.message).join(', '), "] }"), errors);
          return data.product.metafield.value;
        });
      });
      return function (_x2, _x3, _x4) {
        return _ref4.apply(this, arguments);
      };
    }());
    _defineProperty(this, "getVariantMetafield", /*#__PURE__*/function () {
      var _ref6 = _asyncToGenerator(function* (variant_id, namespace, key) {
        var query = "\n            query($id: ID!, $namespace: String!, $key: String!) {\n                node(id: $id) {\n                    ... on ProductVariant {\n                        metafield(namespace: $namespace, key: $key) {\n                            value\n                        }\n                    }\n                }\n            }\n        ";
        var variables = {
          id: "gid://shopify/ProductVariant/".concat(variant_id),
          namespace,
          key
        };
        return _this.request(query, variables).then(_ref7 => {
          var data = _ref7.data,
            errors = _ref7.errors,
            extensions = _ref7.extensions;
          if (errors) throw new Error("".concat(errors.message, " [").concat(errors.graphQLErrors.map(error => error.message).join(', '), "] }"), errors);
          return data.node.metafield.value;
        });
      });
      return function (_x5, _x6, _x7) {
        return _ref6.apply(this, arguments);
      };
    }());
    if (!publicAccessToken || typeof publicAccessToken !== 'string') {
      console.error('Public access token is required and cannot be an empty string.');
      return;
    }
    this.client = createStorefrontApiClient({
      storeDomain: storeDomain,
      apiVersion: apiVersion,
      publicAccessToken: publicAccessToken
    });
  }
}
var storefrontApi = new StorefrontApi();
export { StorefrontApi, storefrontApi };